/**
 * React Hook for PumpPortal WebSocket
 *
 * Provides real-time token feed updates
 */

import { useEffect, useRef, useCallback } from 'react';
import { pumpPortal, normalizeNewToken, normalizeTrade } from '../lib/websocket';
import type { PumpEvent, PumpTokenCreate, PumpTrade } from '../lib/websocket';
import type { TrendingToken } from '../lib/api';

interface UsePumpPortalOptions {
  onNewToken?: (token: TrendingToken) => void;
  onTrade?: (trade: ReturnType<typeof normalizeTrade>) => void;
  enabled?: boolean;
}

export function usePumpPortal({
  onNewToken,
  onTrade,
  enabled = true,
}: UsePumpPortalOptions = {}) {
  const connectedRef = useRef(false);

  const handleNewToken = useCallback((event: PumpEvent) => {
    if (event.txType === 'create' && onNewToken) {
      const normalized = normalizeNewToken(event as PumpTokenCreate);
      onNewToken(normalized);
    }
  }, [onNewToken]);

  const handleTrade = useCallback((event: PumpEvent) => {
    if ((event.txType === 'buy' || event.txType === 'sell') && onTrade) {
      const normalized = normalizeTrade(event as PumpTrade);
      onTrade(normalized);
    }
  }, [onTrade]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const connect = async () => {
      try {
        await pumpPortal.connect();
        connectedRef.current = true;

        // Subscribe to new tokens
        if (onNewToken) {
          pumpPortal.subscribeNewTokens(handleNewToken);
        }

        // Note: For trades, we'd need specific token addresses
        // This would be done per-token when viewing details
      } catch (error) {
        console.error('[usePumpPortal] Connection error:', error);
      }
    };

    connect();

    return () => {
      // Don't disconnect on unmount - keep connection alive for other components
      // The singleton handles reconnection
    };
  }, [enabled, onNewToken, handleNewToken]);

  const subscribeToTokenTrades = useCallback((mints: string[]) => {
    return pumpPortal.subscribeTokenTrades(mints, handleTrade);
  }, [handleTrade]);

  return {
    subscribeToTokenTrades,
    isConnected: connectedRef.current,
  };
}

/**
 * Hook for subscribing to trades of specific tokens
 */
export function useTokenTrades(
  tokenAddresses: string[],
  onTrade: (trade: ReturnType<typeof normalizeTrade>) => void,
  enabled = true
) {
  useEffect(() => {
    if (!enabled || tokenAddresses.length === 0) {
      return;
    }

    const connect = async () => {
      try {
        await pumpPortal.connect();
        return pumpPortal.subscribeTokenTrades(tokenAddresses, (event) => {
          if (event.txType === 'buy' || event.txType === 'sell') {
            const normalized = normalizeTrade(event as PumpTrade);
            onTrade(normalized);
          }
        });
      } catch (error) {
        console.error('[useTokenTrades] Error:', error);
        return () => {};
      }
    };

    let unsubscribe: (() => void) | undefined;

    connect().then(unsub => {
      unsubscribe = unsub;
    });

    return () => {
      unsubscribe?.();
    };
  }, [tokenAddresses.join(','), onTrade, enabled]);
}
